import type React from 'react'
import { useCallback, useEffect, useRef } from 'react'

interface UseEscapeClosesPanelInput {
  panelId: string
  isOpen: boolean
  activePanel: string | null
  closePanel: () => void
  /** Rail icon buttons, keyed by panel — focus returns to `panelId`'s icon. */
  railButtonRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>
  onEscapeFallbackToSelectTool?: () => void
  /** Optional selection tier, checked after the dropdown check and before the panel closes:
   *  deselects a placed canvas object and returns true when it did, so that press stops there. */
  onEscapeClearSelection?: () => boolean
}

/**
 * WMPCM-10936 AC-4/AC-5/AC-6 — Escape priority chain for a tray panel (ClikArt, Backgrounds,
 * Templates): an open dropdown inside the panel closes first (that's Radix's own default Escape
 * behavior on its Popover, which also returns focus to the dropdown's trigger — nothing to do
 * here except NOT also close the panel on the same keypress); otherwise, if the host supplied
 * `onEscapeClearSelection` (ClikArt does) and it deselects a placed canvas object, that press stops
 * there (WMPCM-12123); only then, with nothing else open, the panel itself closes, falls back to
 * the Select tool, and focus returns to its rail icon.
 *
 * The panel reports its dropdowns (and thumbnail hover/focus previews, which are Radix Popovers
 * too) through the returned callback, passed as its `onAnyDropdownOpenChange`. It's tracked in a
 * ref (not state) so the always-registered Escape listener never needs to re-subscribe and never
 * reads a stale value from a captured closure. The callback has a stable identity on purpose: the
 * panels report "no dropdown open" from an effect cleanup keyed on that prop, so an inline arrow
 * (new every render) made every `RightPanel` re-render — e.g. picking a keyword, which updates
 * lifted state there — reset the ref while the keyword dropdown was still open, and the next
 * Escape closed the whole panel too.
 *
 * Capture phase (matching the established pattern in `center-canvas/hooks/use-edit-mode-key-capture.ts`'s single-mode Escape
 * handlers) so this runs before Radix's own bubble-phase Escape handling — but unlike those
 * handlers, this one only ever acts (calls preventDefault) when nothing else is open; when a
 * dropdown is open it does nothing at all, deliberately letting the event continue through to
 * Radix's own listener.
 *
 * Returns the stable `onAnyDropdownOpenChange` callback for the panel.
 */
export function useEscapeClosesPanel({
  panelId,
  isOpen,
  activePanel,
  closePanel,
  railButtonRefs,
  onEscapeFallbackToSelectTool,
  onEscapeClearSelection,
}: UseEscapeClosesPanelInput): (open: boolean) => void {
  const dropdownOpenRef = useRef(false)

  const handleDropdownOpenChange = useCallback((open: boolean) => {
    dropdownOpenRef.current = open
  }, [])

  useEffect(() => {
    if (!isOpen || activePanel !== panelId) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Another layer (a Radix preview/popover, the zoom menu) already took this press. The ref
      // below can't be trusted on its own: a layer that closes ahead of this listener flushes its
      // state, and so resets the ref, before this runs.
      if (e.defaultPrevented) return
      if (dropdownOpenRef.current) return

      if (onEscapeClearSelection?.()) {
        e.preventDefault()
        return
      }

      e.preventDefault()
      closePanel()
      onEscapeFallbackToSelectTool?.()
      // Runs after the panel-closing re-render so the rail button is still mounted/focusable —
      // the rail is always rendered (`PanelRail`), so no waiting on a conditionally-mounted
      // target either way, but the setTimeout keeps this consistent with the other
      // focus-restoration call sites in this codebase (e.g. `focusSelectTool` in
      // `left-panel/root-panel/hooks/use-left-panel-imperative-handle.ts`).
      setTimeout(() => railButtonRefs.current[panelId]?.focus(), 0)
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [
    isOpen,
    activePanel,
    panelId,
    closePanel,
    railButtonRefs,
    onEscapeFallbackToSelectTool,
    onEscapeClearSelection,
  ])

  return handleDropdownOpenChange
}
